import { useQuestionnaireStore } from '@/questionnaire/stores/questionnaire'
import type { LegalDocumentSection, TemplateVariable } from '@/questionnaire/types'
import { getLocalesDateStr, getLocalesMoneyStrValue } from '@/utils/language-formatters'
import countries from '@/utils/countries'

/**
 * Returns the render options for a section
 *
 * @remarks
 * Use this method in order to get the normalized and sanitized render options for a section.
 * These render options are used to render the section through the handlebars template engine.
 *
 * @param section - The section you want to get the render options for
 * @returns A dictionary with the render options for the section. The keys are the template variable codes and the values are the render options converted to strings.
 */
const renderOptions = (section: LegalDocumentSection) => {
  const opts: Record<string, string> = {}
  const questionnaireStore = useQuestionnaireStore()
  Object.values(section.template_variables).forEach(({ id: _id, code }) => {
    const value = questionnaireStore.getTemplateVariableValue(code)
    const templateVariable = questionnaireStore.getTemplateVariable(code)
    const variableKey = questionnaireStore.getTemplateVariableKey(code)

    if (!templateVariable) {
      return
    }

    if (value === null || value === undefined || value === '') {
      opts[code] = `______${templateVariable.label.toUpperCase().replace(' ', '_')}______`
    } else if (typeof value === 'number') {
      opts[code] = value.toString()
    } else if (value instanceof Date) {
      opts[code] = value.toLocaleDateString()
    } else if (value instanceof Array) {
      if (value.length == 2 && value[0] instanceof Date && value[1] instanceof Date) {
        const dateRange = value as [Date, Date]
        opts[code] = `${dateRange[0].toLocaleDateString()} - ${dateRange[1].toLocaleDateString()}`
      } else {
        opts[code] = value.join(', ')
      }
    } else if (typeof value === 'boolean') {
      if (value) {
        opts[code] = 'Ja'
      } else {
        opts[code] = 'Nein'
      }
    } else {
      opts[code] = castAndMapValuesForRendering(templateVariable, value)
    }

    if (variableKey !== code) {
      if (value === null || value === undefined || value === '') {
        return
      } else {
        opts[variableKey] = 'Ja'
      }
    }
  })

  return opts
}

const castAndMapValuesForRendering = (templateVariable: TemplateVariable, value: string) => {
  switch (templateVariable.input_type) {
    case 'boolean':
      return value === 'true' ? 'Ja' : 'Nein'
    case 'currency':
      return getLocalesMoneyStrValue(Number(value), false)
    case 'countryselect':
      return countries.find(({ alpha2 }) => alpha2 === value)?.name || value
    case 'date':
      return getLocalesDateStr(value ? new Date(value) : new Date())
    case 'singleselect':
      return templateVariable.select_options.find(option => option.code === value)?.label || value
    case 'multiselect':
      return templateVariable.select_options.find(option => option.code === value)?.label || value
    default:
      return value
  }
}

export default renderOptions
